import React, { useEffect, useState } from "react";
import Layout from "../layouts/Layout";
import { FONT_HEADING, FONT_UI } from "../layouts/constants";

export default function InstallationModule() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [status, setStatus] = useState("Checking installation...");
  const [statusType, setStatusType] = useState("warning");

  const [checks, setChecks] = useState({
    https: "Checking...",
    manifest: "Checking...",
    serviceWorker: "Checking...",
    device: "Checking...",
  });

  // ===== UTIL =====
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = () =>
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const isPWASupported = () =>
    "serviceWorker" in navigator && "caches" in window;

  // ===== INIT =====
  useEffect(() => {
    checkPWAStatus();

    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setStatus("Ready to install!");
      setStatusType("success");
    });

    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setStatus("App installed successfully!");
      setStatusType("success");
    });
  }, []);

  // ===== STATUS CHECK =====
  const checkPWAStatus = async () => {
    // HTTPS
    const https =
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost"
        ? "Secure ✓"
        : "HTTPS Required";

    // Manifest
    let manifest = "Not Found";
    try {
      const res = await fetch("/manifest.json");
      manifest = res.ok ? "Found ✓" : "Missing";
    } catch {
      manifest = "Error";
    }

    // Service Worker
    let sw = "Not Supported";
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      sw = reg ? "Active ✓" : "Not Registered";
    }

    // Device
    const mobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
    const device = mobile ? "Mobile ✓" : "Desktop";

    setChecks({ https, manifest, serviceWorker: sw, device });

    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone
    ) {
      setIsInstalled(true);
      setStatus("Already Installed");
      setStatusType("success");
    }
  };

  // ===== INSTALL =====
  const handleInstall = async () => {
    if (isIOS() && isSafari()) {
      alert("Use Share → Add to Home Screen");
      return;
    }

    if (!deferredPrompt) {
      alert("Install option not available. Refresh page.");
      return;
    }

    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome === "accepted") {
      setStatus("Installed successfully!");
      setStatusType("success");
    } else {
      setStatus("Installation cancelled");
      setStatusType("warning");
    }

    setDeferredPrompt(null);
  };

  return (
    <Layout activePage="installation">
      {/* HEADER */}
      <div className="mb-6">
        <h2
          className="text-[26px] text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          Installation Module
        </h2>

        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Install your Security Patrol PWA application
        </p>
      </div>

      {/* STATUS ALERT */}
      <div
        className={`mb-5 px-4 py-3 rounded-xl text-sm font-medium ${
          statusType === "success"
            ? "bg-green-50 text-green-700"
            : statusType === "error"
            ? "bg-red-50 text-red-700"
            : "bg-yellow-50 text-yellow-700"
        }`}
        style={{ fontFamily: FONT_UI }}
      >
        {status}
      </div>

      {/* MAIN SECTION */}
      <div className="bg-white rounded-2xl p-6 border shadow-sm">
        <h3
          className="text-[20px] mb-4"
          style={{ fontFamily: FONT_HEADING }}
        >
          PWA Status Check
        </h3>

        {/* STATUS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <StatusCard label="HTTPS" value={checks.https} />
          <StatusCard label="Manifest" value={checks.manifest} />
          <StatusCard label="Service Worker" value={checks.serviceWorker} />
          <StatusCard label="Device" value={checks.device} />
        </div>

        {/* INSTALL BUTTON */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleInstall}
            disabled={isInstalled}
            className="px-5 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg"
            style={{ fontFamily: FONT_UI }}
          >
            {isInstalled ? "Installed ✅" : "Install App"}
          </button>

          <button
            onClick={checkPWAStatus}
            className="px-5 py-2.5 rounded-xl border bg-white"
            style={{ fontFamily: FONT_UI }}
          >
            Re-check
          </button>

          <button
            onClick={() =>
              alert(
                "1. Click Install\n2. Add to Home Screen\n3. Launch App"
              )
            }
            className="px-5 py-2.5 rounded-xl bg-green-600 text-white"
            style={{ fontFamily: FONT_UI }}
          >
            Instructions
          </button>
        </div>
      </div>

      {/* FEATURES */}
      <div className="bg-white rounded-2xl p-6 border shadow-sm mt-6">
        <h3
          className="text-[20px] mb-4"
          style={{ fontFamily: FONT_HEADING }}
        >
          Features
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Feature title="Offline Access" />
          <Feature title="Native Experience" />
          <Feature title="Push Notifications" />
          <Feature title="Home Screen App" />
          <Feature title="Auto Updates" />
          <Feature title="Cross Platform" />
        </div>
      </div>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function StatusCard({ label, value }) {
  return (
    <div className="bg-slate-50 border rounded-xl p-4">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Feature({ title }) {
  return (
    <div className="border rounded-xl p-4 text-center">
      <div className="font-semibold">{title}</div>
    </div>
  );
}